import {MechanicValidator} from './MechanicValidator'
import {ActionStatus, PieceTypes} from '@colonist/game-base-types'
import {getCardsForPiece} from '@colonist/game-card-types'
import {CardEnum, MechanicCityPlayerState, MechanicCityState, NestedReadonly, PlayerColor, TileCornerBuildingType} from '@colonist/game-states'
import {ArrayUtils} from '@colonist/utils'
import {MapValidator} from '../core/map/MapValidator'
import {PlayerValidator} from '@colonist/game-mechanic-validators'

export class MechanicValidatorCity extends MechanicValidator {
    private readonly cityState: MechanicCityState

    constructor(playerControllers: PlayerValidator[], mapState: MapValidator, cityState: MechanicCityState) {
        super(playerControllers, mapState)

        this.cityState = cityState
    }

    getCityPlayerState(player: PlayerColor): NestedReadonly<MechanicCityPlayerState> {
        if(player == PlayerColor.None) {
            return {
                bankCityAmount: 0,
            }
        }
        const cityPlayerState = this.cityState[player]
        if(cityPlayerState === undefined) throw new Error(`No MechanicCityPlayerState for player ${player}`)

        return cityPlayerState
    }

    hasResourcesAndPiecesForCity(player: PlayerColor, useResourcesAndSettlement: boolean, discount?: CardEnum[]): ActionStatus {
        const playerController = this.playerValidators.get(player)
        if(playerController == undefined) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.noPlayer'}}

        const cityPlayerState = this.getCityPlayerState(player)
        if(cityPlayerState.bankCityAmount <= 0) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.notEnoughCityPieces'}}
        if(!useResourcesAndSettlement) return {canTakeAction: true}

        const placedSettlementAmount = this.mapValidator.tileState.getPlacedCornerBuildingCountForPlayer(TileCornerBuildingType.Settlement, player)
        if(placedSettlementAmount <= 0) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.noSettlementToUpgrade'}}

        discount = discount ?? cityPlayerState.nextCityDiscount ?? []
        const resourcesForCity = getCardsForPiece(PieceTypes.City)
        ArrayUtils.removeElementsFromArray(resourcesForCity, discount)
        const hasResourcesForCity = playerController.hasResourceCardsInHand(resourcesForCity)
        if(!hasResourcesForCity) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.notEnoughResources'}}

        return {canTakeAction: true}
    }

    canPlayerPlaceCity(player: PlayerColor): ActionStatus {
        const useResourcesAndSettlement = true
        const doesPlayerHaveResourcesAndPiecesForCity = this.hasResourcesAndPiecesForCity(player, useResourcesAndSettlement)
        const {where} = this.whereCanPlayerPlaceCities(player)

        doesPlayerHaveResourcesAndPiecesForCity.validHoverLocations = where
        if(!doesPlayerHaveResourcesAndPiecesForCity.canTakeAction) {
            return doesPlayerHaveResourcesAndPiecesForCity
        }

        if(where.length <= 0) {
            return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.noWhereToPlaceCity'}, validHoverLocations: where}
        }

        return {canTakeAction: true, validHoverLocations: where}
    }

    whereCanPlayerPlaceCities(player: PlayerColor): {where: number[], cornerBuildingTypeBeingUpgraded: TileCornerBuildingType} {
        const placesToBuildCityArray: number[] = []
        for(const [index, corner] of this.mapValidator.tileState.tileCorners.entries()) {
            if(corner.owner != player) continue

            // Mar 20, 2021. If a player has a destroyed city they NEED to upgrade that city next
            if(corner.buildingType == TileCornerBuildingType.DestroyedCity) return this.whereCanPlayerUpgradeDestroyedCity(player)
            if(corner.buildingType != TileCornerBuildingType.Settlement) continue

            placesToBuildCityArray.push(index)
        }

        return {where: placesToBuildCityArray, cornerBuildingTypeBeingUpgraded: TileCornerBuildingType.Settlement}
    }

    private whereCanPlayerUpgradeDestroyedCity(player: PlayerColor): {where: number[], cornerBuildingTypeBeingUpgraded: TileCornerBuildingType} {
        const whereCanPlayerUpgradeDestroyedCity: number[] = []
        for(const [index, corner] of this.mapValidator.tileState.tileCorners.entries()) {
            if(corner.owner != player) continue

            if(corner.buildingType != TileCornerBuildingType.DestroyedCity) continue

            whereCanPlayerUpgradeDestroyedCity.push(index)
        }

        return {where: whereCanPlayerUpgradeDestroyedCity, cornerBuildingTypeBeingUpgraded: TileCornerBuildingType.DestroyedCity}
    }
}
